import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { ArrowRight, Check } from '@phosphor-icons/react';
import { ALL_VENDOR_CATEGORIES } from '../utils/vendorPartnerData';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

const CATEGORY_IMAGES = {
  dj: 'https://images.unsplash.com/photo-1583906703597-f0a9a127c18a?w=400&q=80',
  catering: 'https://images.unsplash.com/photo-1555244162-803834f70033?w=400&q=80',
  chef: 'https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=400&q=80',
  photography: 'https://images.unsplash.com/photo-1554048612-b6a482bc67e5?w=400&q=80',
  videography: 'https://images.unsplash.com/photo-1579187707643-35646d22b596?w=400&q=80',
  bands: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&q=80',
  bartender: 'https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=400&q=80',
  'photo-booth': 'https://images.unsplash.com/photo-1527529482837-4698179dc6ce?w=400&q=80',
  'live-artist': 'https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?w=400&q=80',
  entertainment: 'https://images.unsplash.com/photo-1639682687092-0264e96cbb71?w=400&q=80',
  venues: 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=400&q=80',
  staffing: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=400&q=80',
};

const venueNames = [
  'The Brown Palace', 'Four Seasons Denver', 'Ellie Caulkins Opera House',
  'Denver Botanic Gardens', 'Blanc Denver', 'The Cable Center',
  'Wellshire Event Center', 'Seawell Grand Ballroom', 'Moss Denver',
];

export default function PartnersHub() {
  return (
    <div className="min-h-screen bg-[#FBFBF9] overflow-hidden" data-testid="partners-hub">
      <Helmet>
        <title>Partner With Eventism | Join Denver's Top Event Platform</title>
        <meta name="description" content="Join Denver's top event services platform as a vendor partner. DJs, caterers, photographers, entertainers — grow your business with Eventism." />
        <link rel="canonical" href="https://eventism.com/partners" />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-28 pb-20 md:pt-36 md:pb-28 px-6 md:px-12 lg:px-24" data-testid="hub-hero">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#FF4400] border border-[#FF4400] px-3 py-1.5 mb-6">
              Vendor Partner Program
            </span>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.9] text-[#0A0A0A] mb-6">
              Grow your business<br />with Denver's events.
            </h1>
            <p className="text-lg md:text-xl text-[#525252] font-medium leading-relaxed mb-10 max-w-2xl mx-auto">
              Whether you're a DJ, caterer, photographer, or entertainer — Eventism connects you with clients, venues, and bookings you can't find on your own.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 bg-[#FF4400] text-white px-8 py-4 font-bold tracking-wide hover:bg-[#E63D00] transition-colors"
              data-testid="hub-hero-cta"
            >
              Apply to Partner <ArrowRight size={20} weight="bold" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Venue Marquee */}
      <section className="bg-[#F0F0EC] py-5 border-y border-[#E5E5E0]">
        <Marquee speed={30} gradient={false} pauseOnHover>
          {venueNames.map((name, i) => (
            <span key={i} className="mx-6 text-sm font-bold uppercase tracking-[0.15em] text-[#8A8A83]">
              {name} <span className="text-[#D4D4CE] mx-2">&bull;</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* Category Grid */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="category-grid">
        <div className="max-w-7xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-16 text-center">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF4400] mb-4 block">Find Your Category</span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.1] text-[#0A0A0A]">
              We're recruiting across<br />every event category.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {ALL_VENDOR_CATEGORIES.map((cat, i) => (
              <motion.div
                key={cat.slug}
                variants={fadeUp}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <Link
                  to={`/partners/${cat.slug}`}
                  className="group block relative overflow-hidden bg-white border border-[#E5E5E0] hover:-translate-y-1 hover:shadow-xl hover:border-[#D4D4CE] transition-all duration-300"
                  data-testid={`category-card-${cat.slug}`}
                >
                  <div className="h-48 overflow-hidden">
                    <img
                      src={CATEGORY_IMAGES[cat.slug] || 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=400&q=80'}
                      alt={cat.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold tracking-tight text-[#0A0A0A] mb-2 group-hover:text-[#FF4400] transition-colors">
                      {cat.title}
                    </h3>
                    <span className="inline-flex items-center gap-1 text-sm font-bold text-[#FF4400]">
                      Learn More <ArrowRight size={14} weight="bold" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Benefits */}
      <section className="bg-[#0A0A0A] text-white py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="hub-benefits">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF4400] mb-4 block">Platform Benefits</span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.1] mb-12">
              Why vendors choose Eventism.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
            {[
              'Free to join — no monthly fees',
              'Access to 50+ venue partners',
              'Professional marketing for your services',
              'Direct bookings from verified clients',
              'Professional vendor profile & portfolio',
              'Payment processing & invoicing included',
              'Flexible scheduling — you set availability',
              'Growing network = more bookings for you',
            ].map((item, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="flex items-center gap-3 p-4 border border-[#333] hover:border-[#FF4400] transition-colors"
              >
                <div className="w-6 h-6 bg-[#FF4400] flex items-center justify-center shrink-0">
                  <Check size={14} weight="bold" className="text-white" />
                </div>
                <span className="font-medium">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#FF4400] py-24 md:py-32 px-6 md:px-12 lg:px-24 text-center" data-testid="hub-final-cta">
        <div className="max-w-3xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.9] text-white mb-6">
              Let's build your<br />event business together.
            </h2>
            <p className="text-white/80 text-lg mb-10 max-w-lg mx-auto">
              Join Denver's fastest-growing event platform. It's free to apply.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 bg-white text-[#FF4400] px-10 py-5 text-lg font-black tracking-wide hover:bg-[#0A0A0A] hover:text-white transition-colors"
              data-testid="hub-final-signup"
            >
              Sign Up Now — It's Free <ArrowRight size={22} weight="bold" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] text-white py-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div>
            <Link to="/" className="text-2xl font-black tracking-tighter">EVENTISM</Link>
            <p className="text-[#8A8A83] text-sm mt-1">Denver's Event Services Platform</p>
          </div>
          <div className="flex items-center gap-6 text-sm text-[#8A8A83]">
            <Link to="/about" className="hover:text-white transition-colors">About</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
